import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calculator, Sparkles, Info } from "lucide-react";
import { cn, inrFull } from "../../lib/utils";
import { track } from "../../lib/analytics";
import { Reveal } from "./parts1";
import { fromConfig, estimateBanded, annualEffectiveRatePaise } from "../../lib/pricingBands";

// Per-student pricing band table + interactive estimator (founder-approved model —
// docs/product/PRICING_TIERS_ROI.md §B). ALL math lives in the pure, unit-tested
// lib/pricingBands.js (integer paise); this component only renders — money is formatted
// exclusively via inrFull() at the edge, never raw paise. Mounted on the dedicated /pricing
// procurement page beneath the fixed-plan catalogue.
//
// The NUMBERS come from the platform-governed pricing model: PricingPage passes the served
// `pricing_model` payload (GET /api/public/marketing) as the `pricingModel` prop and
// `fromConfig` normalizes it — an admin edit at /platform/pricing shows here on the next
// page load, no deploy. The lib's hardcoded constants are the offline fallback only. Evidence
// surfaces validate the response before mounting this estimator, so a failed request is never
// presented as current pricing.

// §B.2 mid-school profile, seeded by band ORDER so the estimator demonstrates itself on
// first paint whatever the configured bands are.
const SEED_COUNTS = [450, 400, 150];
const seedCounts = (bands) =>
  Object.fromEntries(bands.map((b, i) => [b.id, SEED_COUNTS[i] ?? 0]));

// bps → a human percent label ("20%", "12.5%").
const pctLabel = (bps) => `${parseFloat((bps / 100).toFixed(2))}%`;

// Analytics bucket for the modelled TOTAL student count — a size signal, never an exact figure
// (no PII, no school identity; matches the enquiry-form band ceilings).
const studentsBucket = (total) => {
  if (!total) return "0";
  if (total <= 200) return "1-200";
  if (total <= 500) return "201-500";
  if (total <= 1000) return "501-1000";
  if (total <= 2500) return "1001-2500";
  return "2500+";
};

export default function PricingEstimator({
  pricingModel = null,
  embedded = false,
  perStudentOnly = false,
  taxDisplay = "Tax treatment unavailable",
}) {
  const model = useMemo(() => fromConfig(pricingModel), [pricingModel]);
  const [counts, setCounts] = useState(() => seedCounts(model.bands));
  const [cycle, setCycle] = useState("yearly");
  const [aiPack, setAiPack] = useState(false);
  // The served config can arrive AFTER first paint (Landing fetches the CMS async): re-seed any
  // band the current counts don't know, keeping what the visitor already typed for matching ids.
  useEffect(() => {
    setCounts((c) => (model.bands.every((b) => b.id in c) ? c : { ...seedCounts(model.bands), ...c }));
  }, [model]);
  const est = useMemo(
    () => estimateBanded(counts, cycle, { aiPack: perStudentOnly ? false : aiPack, model }),
    [counts, cycle, aiPack, model, perStudentOnly],
  );
  // Phase-1 handoff (2026-08-18): ONE debounced usage event per editing burst —
  // {band, students_bucket} only (a bucketed size signal; no head count, no PII).
  const estRef = useRef(est);
  estRef.current = est;
  const usedTimer = useRef(null);
  const lastBand = useRef("");
  const noteUsed = (bandId) => {
    lastBand.current = bandId;
    if (usedTimer.current) window.clearTimeout(usedTimer.current);
    usedTimer.current = window.setTimeout(() => {
      usedTimer.current = null;
      track("pricing_estimator_used", {
        band: lastBand.current,
        students_bucket: studentsBucket(estRef.current.students),
      });
    }, 800);
  };
  useEffect(() => () => { if (usedTimer.current) window.clearTimeout(usedTimer.current); }, []);
  const yearly = cycle === "yearly";
  const savingsPaise = est.monthlyPaise * 12 - est.annualPaise; // the annual-prepay saving
  const discountLabel = pctLabel(model.annualDiscountBps);
  const resolvedTaxDisplay = typeof taxDisplay === "string" && taxDisplay.trim()
    ? taxDisplay.trim()
    : "Tax treatment unavailable";
  const cycles = [
    { id: "monthly", label: "Monthly estimate" },
    { id: "yearly", label: `Annual estimate · ${discountLabel} policy` },
  ];

  const estimator = (
      <div className="overflow-hidden rounded-[28px] border border-line bg-surface shadow-[0_20px_50px_rgba(15,23,42,0.08)]" data-testid="pricing-estimator">
        <div className="grid lg:grid-cols-2">
          {/* ---- the band table: what a student costs, by class band ---- */}
          <div className="p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-line">
            <h3 className="font-heading font-bold text-ink text-[20px] tracking-tight flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-primary/10 ring-1 ring-primary/15 grid place-items-center shrink-0">
                <Calculator className="w-[18px] h-[18px] text-primary" aria-hidden="true" />
              </span>
              Proposal by student, by class band
            </h3>
            <p className="text-[14.5px] text-ink-2 mt-2 leading-relaxed">
              {perStudentOnly
                ? `Model the students you expect to serve by class band. The annual proposal policy reduces twelve monthly estimates by ${discountLabel}.`
                : `Model a Growth proposal from the students you expect to serve. The annual proposal policy reduces twelve monthly estimates by ${discountLabel}.`}
            </p>
            <div className="mt-5 overflow-x-auto overscroll-contain" role="region" aria-label="Per-student class-band proposal estimates, horizontally scrollable" tabIndex={0}>
              <table className="w-full text-sm" data-testid="pricing-band-table">
                <caption className="sr-only">Per-student monthly proposal estimate by class band, with the annual-proposal effective estimate</caption>
                <thead>
                  <tr className="text-left border-b border-line">
                    <th scope="col" className="py-2.5 pr-3 font-semibold text-ink">Class band</th>
                    <th scope="col" className="py-2.5 pr-3 font-semibold text-ink">Monthly estimate</th>
                    <th scope="col" className="py-2.5 font-semibold text-ink">Annual estimate <span className="text-primary whitespace-nowrap">({discountLabel} policy)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {model.bands.map((b) => (
                    <tr key={b.id} className="border-b border-line last:border-b-0">
                      <th scope="row" className="py-3 pr-3 font-medium text-ink-2 text-left">{b.label}</th>
                      <td className="py-3 pr-3">
                        <span className="font-heading font-bold text-ink text-[17px]">{inrFull(b.ratePaise)}</span>
                        <span className="text-ink-subtle text-[12.5px]"> /student/mo</span>
                      </td>
                      <td className="py-3">
                        <span className="font-heading font-bold text-primary text-[17px]">{inrFull(annualEffectiveRatePaise(b.ratePaise, model.annualDiscountBps))}</span>
                        <span className="text-ink-subtle text-[12.5px]"> /student/mo effective</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="mt-5 space-y-2 text-[13.5px] text-ink-2">
              <li className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                <span>The planning model applies a <span className="font-semibold text-ink">{inrFull(model.floorMonthlyPaise)}/mo</span> proposal minimum for smaller schools.</span>
              </li>
              {!perStudentOnly && <li className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-ai shrink-0 mt-0.5" aria-hidden="true" />
                <span>A Premium proposal can include one flat <span className="font-semibold text-ink">{inrFull(model.aiPackMonthlyPaise)}/mo</span> AI &amp; Automation pack per school — never per student.</span>
              </li>}
            </ul>
          </div>

          {/* ---- the estimator: students in → the school's price out ---- */}
          <div className="p-6 sm:p-8 bg-muted/50">
            <h3 className="font-heading font-bold text-ink text-[20px] tracking-tight">Model your reviewed proposal</h3>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {model.bands.map((b) => (
                <div key={b.id}>
                  <label htmlFor={`est-${b.id}`} className="block text-[12.5px] font-semibold text-ink-2 mb-1.5">{b.label}</label>
                  <input
                    id={`est-${b.id}`} data-testid={`est-${b.id}`} type="number" inputMode="numeric" min="0" step="1"
                    name={`per_student_count_${b.id}`} autoComplete="off"
                    value={counts[b.id] ?? ""}
                    onChange={(e) => { setCounts((s) => ({ ...s, [b.id]: e.target.value })); noteUsed(b.id); }}
                    className="w-full h-11 rounded-xl border border-line bg-surface px-3 text-[15px] font-semibold text-ink tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary"
                    aria-describedby={`est-${b.id}-rate`}
                  />
                  <p id={`est-${b.id}-rate`} className="text-[11.5px] text-ink-subtle mt-1">students × {inrFull(b.ratePaise)}/mo</p>
                </div>
              ))}
            </div>

            <fieldset className="mt-5">
              <legend className="text-[12.5px] font-semibold text-ink-2 mb-1.5">Proposal cycle</legend>
              <div className="inline-flex rounded-full border border-line bg-surface p-1" role="presentation">
                {cycles.map((c) => (
                  <label key={c.id} data-testid={`est-cycle-${c.id}-option`} className={cn(
                    "cursor-pointer rounded-full px-4 py-1.5 text-[13.5px] font-semibold transition-colors select-none",
                    "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40",
                    cycle === c.id ? "bg-primary text-primary-fg shadow-sm" : "text-ink-2 hover:text-ink"
                  )}>
                    <input type="radio" name="est-cycle" value={c.id} checked={cycle === c.id}
                      onChange={() => setCycle(c.id)} className="sr-only" data-testid={`est-cycle-${c.id}`} />
                    {c.label}
                  </label>
                ))}
              </div>
            </fieldset>

            {!perStudentOnly && <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none">
              <input type="checkbox" checked={aiPack} onChange={(e) => setAiPack(e.target.checked)}
                data-testid="est-ai-pack"
                className="mt-0.5 w-4 h-4 rounded border-line text-ai accent-[#7C3AED] focus-visible:ring-2 focus-visible:ring-ai/40" />
              <span className="text-[13.5px] text-ink-2 leading-snug">
                Include the <span className="font-semibold text-ai-ink">Premium AI &amp; Automation pack</span> in the estimate — flat {inrFull(model.aiPackMonthlyPaise)}/mo per school
              </span>
            </label>}

            {/* result — recomputed live; announced politely to AT */}
            <div aria-live="polite" className="mt-6 rounded-2xl border border-primary/20 bg-primary/[0.05] p-5" data-testid="est-result">
              <p className="text-[12.5px] font-semibold uppercase tracking-wide text-ink-subtle">
                {est.students.toLocaleString("en-IN")} students · {yearly ? "annual proposal estimate" : "monthly proposal estimate"}
              </p>
              <p className="mt-1.5 flex items-baseline gap-1.5 flex-wrap">
                <span className="font-heading font-extrabold text-[34px] text-ink tracking-tight leading-none" data-testid="est-total">{inrFull(est.totalPaise)}</span>
                <span className="text-[15px] font-medium text-ink-subtle">{yearly ? "/year estimate" : "/month estimate"}</span>
              </p>
              {yearly && est.totalPaise > 0 && (
                <p className="text-[13.5px] text-ink-2 mt-2">
                  ≈ <span className="font-semibold text-ink">{inrFull(est.effectiveMonthlyPaise)}/mo</span> effective — the annual proposal is <span className="font-semibold text-primary">{inrFull(savingsPaise)}</span> below twelve monthly estimates.
                </p>
              )}
              {est.floorApplied && (
                <p className="text-[13px] text-ink-2 mt-2" data-testid="est-floor-note">
                  The {inrFull(model.floorMonthlyPaise)}/mo proposal minimum applies at this size.
                </p>
              )}
              <p className="text-[12px] text-ink-subtle mt-2.5">Tax display: {resolvedTaxDisplay}. This planning result is not a checkout price; confirm the commercial amount in an approved written proposal.</p>
            </div>
          </div>
        </div>
      </div>
  );
  return embedded ? estimator : <Reveal className="mt-10">{estimator}</Reveal>;
}
